import React from 'react';
import { Box, Typography, Skeleton, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { StatusChip, type StatusTone } from '../../../components/ui';
import { commandDashboard } from '../commandDashboardTokens';
import type { ActivityRow } from '../types';

interface ActivityListProps {
  title: string;
  rows: ActivityRow[];
  loading?: boolean;
  emptyLabel?: string;
}

/** Tone for each tag `commandDashboardTokens.tagPalette` colours by hand. */
const TAG_TONE: Record<string, StatusTone> = {
  Running: 'info',
  Scheduled: 'info',
  Vendor: 'info',
  Done: 'positive',
  Store: 'positive',
  Failed: 'problem',
  Price: 'problem',
  Job: 'problem',
  Rebate: 'caution',
  User: 'neutral',
  Catalog: 'neutral',
};

export const ActivityList: React.FC<ActivityListProps> = ({ title, rows, loading, emptyLabel = 'No activity in the last 30 days.' }) => (
  <Box
    sx={{
      bgcolor: commandDashboard.color.innerCard,
      border: `1px solid ${commandDashboard.color.innerCardBorder}`,
      borderRadius: `${commandDashboard.radius.innerCard}px`,
      overflow: 'hidden',
    }}
  >
    <Box sx={{ p: '14px 16px', borderBottom: `1px solid ${commandDashboard.color.dividerStrong}` }}>
      <Typography sx={{ fontSize: '14.5px', fontWeight: 700 }}>{title}</Typography>
    </Box>
    {loading ? (
      <Box sx={{ p: '10px 16px' }}>
        {[0, 1, 2].map((i) => (
          <Skeleton key={i} variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    ) : rows.length === 0 ? (
      <Box sx={{ p: '20px 16px', textAlign: 'center' }}>
        <Typography sx={{ fontSize: '13px', color: commandDashboard.color.inkSubtle }}>{emptyLabel}</Typography>
      </Box>
    ) : (
      rows.map((row) => {
        const tone = TAG_TONE[row.tag] ?? 'neutral';
        const repeats = row.count ?? 1;
        return (
          <Box key={row.id} sx={{ display: 'flex', alignItems: 'center', gap: '11px', p: '10px 16px', borderBottom: `1px solid ${commandDashboard.color.dividerLight}` }}>
            <StatusChip label={row.tag} tone={tone} size="small" sx={{ flex: '0 0 auto', fontWeight: 700 }} />
            <Tooltip title={row.text} placement="top-start" enterDelay={300}>
              <Typography
                {...(row.href ? { component: RouterLink, to: row.href } : {})}
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                  fontSize: '13px',
                  color: commandDashboard.color.inkSecondary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textDecoration: 'none',
                  '&:hover': row.href ? { textDecoration: 'underline' } : undefined,
                }}
              >
                {row.text}
              </Typography>
            </Tooltip>
            {repeats > 1 && (
              <Typography
                title={`${repeats} identical runs in a row`}
                sx={{ flex: '0 0 auto', fontSize: '12px', fontWeight: 700, color: commandDashboard.color.inkMuted, fontVariantNumeric: 'tabular-nums' }}
              >
                {`×${repeats}`}
              </Typography>
            )}
            <Typography sx={{ fontSize: '11.5px', color: commandDashboard.color.inkFaint, flex: '0 0 auto' }}>{row.when}</Typography>
          </Box>
        );
      })
    )}
  </Box>
);
